import dayjs from 'dayjs'
import { AnimatePresence, motion } from 'framer-motion'
import { Howl } from 'howler'
import { floor } from 'lodash'
import { SendHorizonal } from 'lucide-react'
import React, { useMemo } from 'react'
import {
  buildStyles,
  CircularProgressbarWithChildren
} from 'react-circular-progressbar'
import RecordingAudioBackdrop from '@/components/chat/RecordingAudioBackdrop'
import {
  Button,
  Column,
  Flex,
  IconButton,
  Show,
  Text,
  Tooltip
} from '@/components/core'
import { useMediaDevices } from '@/contexts/MediaDevicesContext.tsx'
import { classNameBuilder } from '@/helpers'
import { useTimer } from '@/hooks/useTimer.ts'
import 'react-circular-progressbar/dist/styles.css'
import WaveSurfer, { WaveSurferOptions } from 'wavesurfer.js'
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js'

const MAX_RECORD_TIME_IN_SECONDS = 90

export type RecordingAudioVariantType = 'primary' | 'secondary'
interface RecordingAudioProps {
  variant?: RecordingAudioVariantType
  autoTurnOff?: boolean
  onRecordingEnd?: (audioBlob: Blob, seconds: number) => void
  onTooltipConfirm?: (audioBlob: Blob, seconds: number) => void
  pending?: boolean
  autoRecord?: boolean
  waveHeight?: number
  waveScalling?: number
  showRecordingTimeRecommendationTooltip?: boolean
}

function CircularProgressWithTimer({
  seconds,
  running = true
}: {
  seconds: number
  running?: boolean
}) {
  const timerStatus = useMemo(() => {
    if (seconds < 20) {
      return 'short'
    }
    if (seconds < 80) {
      return 'good'
    }

    return 'finish'
  }, [seconds])

  return (
    <Flex className="relative z-40 h-[76px] w-[76px] shrink-0 items-center justify-center">
      <div className="absolute h-[76px] w-[76px] shrink-0 rounded-[100%] bg-white" />
      <CircularProgressbarWithChildren
        value={100}
        strokeWidth={6}
        styles={buildStyles({
          pathColor: timerColorMap.short,
          trailColor: 'transparent',
          strokeLinecap: 'butt'
        })}
      >
        <CircularProgressbarWithChildren
          value={58}
          strokeWidth={6}
          styles={buildStyles({
            pathColor: timerColorMap.good,
            trailColor: 'transparent',
            strokeLinecap: 'butt',
            rotation: 0.22
          })}
        >
          <CircularProgressbarWithChildren
            value={20}
            strokeWidth={6}
            styles={buildStyles({
              pathColor: timerColorMap.finish,
              trailColor: 'transparent',
              strokeLinecap: 'butt',
              rotation: 0.8
            })}
          >
            <Flex className="flex-col items-center justify-center">
              <div
                className={classNameBuilder(
                  'text-sm font-bold transition-colors',
                  timerTextVariantMap[timerStatus]
                )}
              >
                {dayjs(0).set('s', seconds).format('mm:ss')}
              </div>
              <div
                className={classNameBuilder(
                  'text-xs font-normal capitalize transition-colors',
                  timerTextVariantMap[timerStatus]
                )}
              >
                {timerStatus}
              </div>
            </Flex>
          </CircularProgressbarWithChildren>
        </CircularProgressbarWithChildren>
      </CircularProgressbarWithChildren>
      <div
        className="delay absolute h-[66px] w-[66px] shrink-0 rotate-[0deg] animate-[rotate_90s_linear_1]"
        style={{
          ...(!running && {
            animationDelay: `-${seconds}s`,
            animationPlayState: 'paused'
          })
        }}
      >
        <AnimatePresence>
          {![20 * 1000, 80 * 1000].includes(seconds * 1000) && (
            <motion.div
              className={classNameBuilder(
                'flex h-[16px] w-[16px] shrink-0 flex-col items-center justify-center rounded-full',
                ringColorMap[timerStatus]
              )}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.2 }}
            >
              <motion.div
                className={classNameBuilder(
                  'h-[10px] w-[10px] shrink-0 rounded-full',
                  animationVariantMap[timerStatus]
                )}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
              ></motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Flex>
  )
}

const RecordingAudio: React.FC<RecordingAudioProps> = ({
  variant = 'primary',
  autoTurnOff,
  onRecordingEnd,
  onTooltipConfirm,
  pending,
  autoRecord = true,
  waveHeight = 64,
  waveScalling = 2,
  showRecordingTimeRecommendationTooltip
}) => {
  const waveSurferContainerRef = React.useRef<HTMLDivElement>(null)
  const waveSurferRef = React.useRef<RecordPlugin | null>(null)
  const effectAudioRef = React.useRef<Howl | null>(null)
  const secondsRef = React.useRef<number>(0)
  const blobRef = React.useRef<Blob | null>(null)
  const { requestMicrophonePermission } = useMediaDevices()

  const waveSurferOptions = (ref: HTMLDivElement | null) =>
    ({
      container: ref,
      waveColor: waveColorMap[variant],
      barGap: 6,
      cursorColor: 'transparent',
      barWidth: 4,
      barRadius: 4,
      height: waveHeight,
      barHeight: waveScalling
    }) as WaveSurferOptions

  const handleRecord = () => {
    if (autoTurnOff) start()
    if (waveSurferRef.current?.isRecording()) {
      waveSurferRef?.current?.on('record-end', (blob) => {
        blobRef.current = blob
        stopTimer()
        onRecordingEnd?.(blob, secondsRef.current)
      })
      waveSurferRef?.current?.stopRecording()
      waveSurferRef?.current?.stopMic()
      return
    }
    effectAudioRef.current?.play()
    setTimeout(() => waveSurferRef?.current?.startRecording(), 200)
    return
  }

  const {
    start,
    currentTime,
    stop: stopTimer
  } = useTimer({
    endTime: MAX_RECORD_TIME_IN_SECONDS * 1000,
    onTimerEnds: handleRecord,
    refreshRate: 250
  })

  const seconds = currentTime / 1000
  secondsRef.current = seconds

  React.useEffect(() => {
    requestMicrophonePermission()
    if (!effectAudioRef.current) {
      effectAudioRef.current = new Howl({
        src: ['/audio/start-record.mp3'],
        html5: true,
        onloaderror: (_, error) => console.log(error),
        onplayerror: (_, error) => console.log(error)
      })
    }
    if (!waveSurferRef.current) {
      const options = waveSurferOptions(waveSurferContainerRef.current)
      waveSurferRef.current = WaveSurfer.create(options).registerPlugin(
        RecordPlugin.create()
      )

      if (autoRecord) {
        handleRecord()
      }
    }

    return () => {
      if (effectAudioRef.current) {
        effectAudioRef.current.unload()
      }
      if (waveSurferRef.current) {
        waveSurferRef.current.destroy()
      }
    }
  }, [])

  const handleGotIt = React.useCallback(() => {
    if (blobRef.current) {
      onTooltipConfirm?.(blobRef.current, secondsRef.current)
    }
  }, [])

  const buttonComponent =
    variant === 'primary' ? (
      <IconButton
        pending={pending}
        color={variant}
        size="80"
        icon={<SendHorizonal height={32} width={32} />}
        onClick={handleRecord}
      />
    ) : (
      <Button.Translated
        pending={pending}
        color={'quaternary'}
        translationKey="send_button_recording_audio"
        icon={<SendHorizonal height={20} width={20} />}
        size="sm"
        onClick={handleRecord}
        className="px-8 py-1.5"
      />
    )

  return (
    <>
      <Show when={autoTurnOff}>
        <Show
          when={showRecordingTimeRecommendationTooltip}
          fallback={<CircularProgressWithTimer seconds={seconds} />}
        >
          <>
            <RecordingAudioBackdrop />
            <Tooltip
              className="!visible !opacity-100"
              content={
                <Column className="w-[310px] justify-center gap-5">
                  <Text.Translated
                    translationKey="twenty_seconds_recording_audio"
                    values={[floor(seconds)]}
                    className="text-center"
                  />
                  <Button.Translated
                    translationKey="got_it_button_recording_audio"
                    onClick={handleGotIt}
                    size="lg"
                  />
                </Column>
              }
            >
              <CircularProgressWithTimer seconds={seconds} running={false} />
            </Tooltip>
          </>
        </Show>
      </Show>
      <div
        ref={waveSurferContainerRef}
        className="flex h-12 w-full flex-col justify-center overflow-hidden px-4 pl-6"
      />
      {buttonComponent}
    </>
  )
}

export default RecordingAudio

const waveColorMap: Record<RecordingAudioVariantType, string> = {
  primary: '#9B5EFD',
  secondary: '#A1A1A1'
}

const timerColorMap: Record<'short' | 'good' | 'finish', string> = {
  short: '#E4E4E7',
  good: '#14B8A6',
  finish: '#EA580C'
}

const ringColorMap: Record<'short' | 'good' | 'finish', string> = {
  short: 'bg-[#A1A1A1]/[.30]',
  good: 'bg-[#14B8A6]/[.30]',
  finish: 'bg-[#EA580C]/[.30]'
}

const timerTextVariantMap: Record<'short' | 'good' | 'finish', string> = {
  short: 'text-[#A1A1A1]',
  good: 'text-[#0F766E]',
  finish: 'text-[#9A3412]'
}

const animationVariantMap: Record<'short' | 'good' | 'finish', string> = {
  short: 'bg-[#A1A1A1]',
  good: 'bg-[#0F766E]',
  finish: 'bg-[#9A3412]'
}
